import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { getActiveHostSession, startHostSession } from '../lib/meetings';

const STORAGE_KEY = 'nbna.meeting.hostMode.v1';

function safeParseJson(raw) {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function readHostMode() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = safeParseJson(raw);
    if (!parsed || !parsed.qrToken || !parsed.expiresAt) return null;
    const expiresAtMs = Date.parse(parsed.expiresAt);
    if (!Number.isFinite(expiresAtMs)) return null;
    if (expiresAtMs <= Date.now()) return null;
    return {
      qrToken: String(parsed.qrToken),
      expiresAt: new Date(expiresAtMs).toISOString(),
    };
  } catch {
    return null;
  }
}

function writeHostMode(record) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // ignore
  }
}

function clearHostMode() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

export default function MeetingHost() {
  const navigate = useNavigate();

  const [status, setStatus] = useState('loading');
  const [expiresAt, setExpiresAt] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [warningMessage, setWarningMessage] = useState('');

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const cached = readHostMode();
        let data = await getActiveHostSession();
        const active = data?.hostSession;

        if (!active) {
          clearHostMode();
          data = await startHostSession();
        } else if (cached && cached.qrToken !== String(active.qrToken ?? '')) {
          clearHostMode();
        }

        const token = String(data?.hostSession?.qrToken ?? '');
        const exp = String(data?.hostSession?.expiresAt ?? '');
        if (!token || !exp) {
          throw new Error('Host session did not return qrToken/expiresAt');
        }

        // Persist host mode first so we can recover after crashes/reloads.
        const record = { qrToken: token, expiresAt: exp };
        writeHostMode(record);

        // Auto-register the host as an attendee.
        try {
          const { apiClient } = await import('../lib/api/client');
          await apiClient.post('/api/meetings/registrations/qr', { qrToken: token });
        } catch (registerError) {
          console.warn('[meeting-host] host auto-registration failed:', registerError);
          if (!cancelled) {
            setWarningMessage(registerError?.message || 'Host attendance could not be registered. Attendee QR remains active.');
          }
        }

        if (!cancelled) {
          setExpiresAt(exp);
          setStatus('ready');
        }
      } catch (error) {
        console.error('[meeting-host] start host session failed:', error);
        if (!cancelled) {
          setStatus('error');
          const status = error?.status ? ` (status ${error.status})` : '';
          setErrorMessage(`Unable to start host mode${status}.`);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const qrUrl = useMemo(() => {
    const origin = window.location.origin;
    // The same address as the printed code at the door. No per-week token:
    // the server resolves which meeting this is, and a cancelled meeting
    // closes it. On-screen and on-paper must agree, or the two drift.
    const registrationUrl = `${origin}/meeting/register`;
    // External QR render service keeps the frontend dependency-free.
    return `https://api.qrserver.com/v1/create-qr-code/?size=420x420&data=${encodeURIComponent(registrationUrl)}`;
  }, []);

  function onExit() {
    clearHostMode();
    navigate('/', { replace: true });
  }

  return (
    <div className="min-h-[calc(100vh-180px)] flex flex-col items-center justify-center px-6 py-10">
      {status === 'loading' ? (
        <div className="text-gray-700">Starting host mode…</div>
      ) : null}

      {status === 'error' ? (
        <div className="max-w-lg w-full rounded-lg border bg-white p-6 shadow-sm">
          <h1 className="text-xl font-semibold text-NBNA-primary">Host Mode</h1>
          <p className="mt-2 text-gray-700">{errorMessage || 'Unable to start host mode.'}</p>
          <button type="button" className="mt-4 btn-primary" onClick={onExit}>
            Back
          </button>
        </div>
      ) : null}

      {status === 'ready' ? (
        <div className="w-full max-w-2xl flex flex-col items-center">
          <h1 className="text-2xl font-semibold text-NBNA-primary">Meeting Registration (Host Mode)</h1>
          <p className="mt-2 text-gray-700 text-center">
            Ask attendees to scan this QR code to register.
          </p>
          {warningMessage ? (
            <p className="mt-3 max-w-xl rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
              {warningMessage}
            </p>
          ) : null}

          <div className="mt-6 rounded-2xl border bg-white p-6 shadow-sm">
            {qrUrl ? (
              <img
                src={qrUrl}
                alt="Meeting registration QR code"
                width={420}
                height={420}
                className="block"
              />
            ) : (
              <div className="text-gray-700">Generating QR…</div>
            )}
          </div>

          <p className="mt-4 text-sm text-gray-600">Host session expires: {expiresAt}</p>

          <button type="button" className="mt-6 btn-primary" onClick={onExit}>
            Exit Host Mode
          </button>
        </div>
      ) : null}
    </div>
  );
}
